import { useEffect, useId, useMemo, useRef, useState } from "react";
import { calculateFinancialsUnified as calculateFinancials } from "@/lib/roi-model";
import { DEFAULT_INPUTS, money } from "@/lib/finance";
import { useSite } from "@/context/SiteContext";

function AnimatedMoney({ value }) {
  const [shown, setShown] = useState(value);
  const previous = useRef(value);
  useEffect(() => {
    const startValue = previous.current;
    previous.current = value;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      setShown(value);
      return;
    }
    let frame = 0;
    let start;
    const tick = (time) => {
      if (start === undefined) start = time;
      const t = Math.min(1, (time - start) / 320);
      setShown(startValue + (value - startValue) * (1 - Math.pow(1 - t, 3)));
      if (t < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [value]);
  return <span aria-label={money(value)}>{money(shown)}</span>;
}

function Tip({ label, children }) {
  return (
    <details className="sbb-tip">
      <summary>{label}</summary>
      <div>{children}</div>
    </details>
  );
}

export function DropZoneLite({ accept = ".pdf,.jpg,.jpeg,.png", multiple = true, maxSize, maxFiles, onDrop, disabled, className, children, ariaLabel, testId }) {
  const [dragActive, setDragActive] = useState(false);
  const depth = useRef(0);
  const inputRef = useRef(null);
  const handle = (fileList) => {
    const files = Array.from(fileList || []);
    const accepted = [];
    const rejected = [];
    for (const f of files) {
      const okType = /\.(pdf|jpe?g|png)$/i.test(f.name) && (!f.type || ["application/pdf", "image/jpeg", "image/png"].includes(f.type));
      const okSize = maxSize ? f.size <= maxSize : true;
      if (okType && okSize) accepted.push(f);
      else rejected.push(f);
    }
    if (multiple && maxFiles && accepted.length > maxFiles) rejected.push(...accepted.splice(maxFiles));
    onDrop(accepted, rejected);
  };
  return (
    <div
      className={`${className} ${dragActive ? "sbb-drag" : ""}`}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      data-testid={testId}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(e) => { if ((e.key === "Enter" || e.key === " ") && !disabled) { e.preventDefault(); inputRef.current?.click(); } }}
      onDragEnter={(e) => { e.preventDefault(); depth.current += 1; setDragActive(true); }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={() => { depth.current -= 1; if (depth.current <= 0) { depth.current = 0; setDragActive(false); } }}
      onDrop={(e) => { e.preventDefault(); depth.current = 0; setDragActive(false); if (!disabled) handle(e.dataTransfer.files); }}
    >
      <input ref={inputRef} type="file" accept={accept} multiple={multiple} style={{ display: "none" }} onChange={(e) => { handle(e.target.files); e.target.value = ""; }} />
      {children(dragActive)}
    </div>
  );
}

/** percent slider/number pair backed by a 0-1 state value */
function PercentField({ label, hint, value, onChange, min = 0, max = 100, step = 1, id }) {
  return (
    <label className="sbb-field" htmlFor={id}>
      <span>
        {label}: <strong>{Math.round(value * 100)}%</strong>
        {hint ? <em style={{ fontWeight: 500, display: "block" }}>{hint}</em> : null}
      </span>
      <input
        id={id}
        type="range"
        min={min}
        max={max}
        step={step}
        value={Math.round(value * 100)}
        onChange={(e) => onChange(Number(e.target.value) / 100)}
        data-testid={id}
      />
      <span className="sbb-hint">Percent of a relevant base — see the tooltip in “Adjust my financial assumptions.”</span>
    </label>
  );
}

export default function FourPillarsEngine({ variant = "full" }) {
  const id = useId();
  const { config, openAudit, content } = useSite();
  const [mode, setMode] = useState("example"); // "example" until the visitor changes something
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);
  const [x, setX] = useState(() => ({ ...DEFAULT_INPUTS, eligibilityConfirmed: true, taxBenefitsUsable: true }));
  const isExample = mode === "example";
  const result = useMemo(
    () => calculateFinancials(isExample ? { ...x, eligibilityConfirmed: true, taxBenefitsUsable: true } : x),
    [x, isExample],
  );

  function update(key, value) {
    if (isExample) {
      // The first real input starts the visitor's own honest scenario.
      setMode("yours");
      setX((current) => ({ ...current, [key]: value, eligibilityConfirmed: false, taxBenefitsUsable: false }));
    } else {
      setX((current) => ({ ...current, [key]: value }));
    }
  }
  function resetExample() {
    setX({ ...DEFAULT_INPUTS, eligibilityConfirmed: true, taxBenefitsUsable: true });
    setMode("example");
    setAssumptionsOpen(false);
  }
  function checkEligibility() {
    setAssumptionsOpen(true);
    requestAnimationFrame(() => document.getElementById("pillar-assumptions")?.scrollIntoView({ behavior: "smooth", block: "start" }));
  }
  const snapshot = {
    ...x, scenarioVersion: "florida-cash-v1", scenarioMode: mode, originPath: typeof window !== "undefined" ? window.location.pathname : "/",
    scenario: {
      systemKW: result.systemKW, grossCost: result.grossCost, npv25: result.npv25,
      paybackYears: result.simplePayback, taxScheduleConfirmed: Boolean(x.eligibilityConfirmed && x.taxBenefitsUsable),
      utility: x.utility, sourceCheckDate: content?.sourceCheckDate || null, modelVersion: "florida-cash-v1",
    },
    grossCost: result.grossCost,
    annualNetOperatingSavings: result.annualNetOperatingSavings,
    taxShield: result.taxShield, simplePayback: result.simplePayback,
  };
  function numberField(label, key, min, max, step = 1) {
    return (
      <label className="sbb-field" key={key}>
        <span>{label}</span>
        <input type="number" min={min} max={max} step={step} value={String(x[key])}
          onChange={(e) => { const n = e.target.valueAsNumber; if (Number.isFinite(n)) update(key, Math.max(min, Math.min(max, n))); }} />
      </label>
    );
  }
  return (
    <section className="sbb-engine" aria-label="Commercial solar financial dashboard" data-testid="four-pillars-engine">
      {variant === "full" && (
        <header className="sbb-hero">
          <div className="sbb-hero-copy">
            <span className="sbb-eyebrow">COASTAL ENERGY · SOLAR BLACK BELT</span>
            <h2>Put your power bill to work for your business.</h2>
            <p>Four financial levers. One bill-based plan.</p>
            <div className="sbb-hero-actions">
              <button type="button" className="sbb-primary" onClick={() => openAudit(snapshot)} data-testid="engine-upload-bill">Upload Your Commercial Electric Bill</button>
              <a href="#financial-calculators">Explore my solar ROI ↓</a>
              <a href={config.phoneHref}>{config.phone}</a>
            </div>
          </div>
        </header>
      )}
      <nav className="sbb-calculator-nav" aria-label="Financial calculators">
        <a href="#financial-calculators">☀ Solar sizing</a><a href="#pillar-tax">Potential tax benefits</a>
        <a href="#pillar-demand">Demand-charge reduction</a><a href="#pillar-capital">Ownership & financing</a>
        <a href="#pillar-return">Payback & long-term returns</a>
      </nav>
      <div id="financial-calculators" className="sbb-dashboard">
        <div className="sbb-section-head">
          <div>
            <span className="sbb-eyebrow">{isExample ? "EXAMPLE SCENARIO" : "YOUR SCENARIO"}</span>
            <h2>Move the numbers. See the opportunity.</h2>
            <p className="sbb-mode-note" data-testid="engine-mode-note">
              {isExample
                ? "Illustrative example: a 30,000 sq ft facility spending $10,000 a month, with tax eligibility assumed confirmed. Enter your details to calculate — your numbers replace the example."
                : "These numbers use your inputs. Illustrative only — not a quote or verified result."}
            </p>
          </div>
          {isExample ? (
            <span className="sbb-estimate">Example—not a quote or verified result</span>
          ) : (
            <button type="button" className="sbb-estimate" style={{ cursor: "pointer" }} onClick={resetExample} data-testid="engine-reset-example">Reset to example</button>
          )}
        </div>
        <div className="sbb-controls">
          <label className="sbb-field" htmlFor={`${id}-spend`}>
            <span>Monthly power spend: <strong>{money(x.monthlySpend)}</strong></span>
            <input id={`${id}-spend`} type="range" min={500} max={100000} step={100} value={x.monthlySpend} data-testid="engine-spend" onChange={(e) => update("monthlySpend", Number(e.target.value))} />
          </label>
          {numberField("Usable roof area (sq ft)", "roofSqFt", 100, 10000000, 100)}
          <label className="sbb-field">
            <span>Compare capital paths</span>
            <select value={x.financing} data-testid="engine-financing" onChange={(e) => update("financing", e.target.value)}>
              <option value="cash">Own with cash</option>
              <option value="cpace">C-PACE financing scenario</option>
              <option value="ppa">PPA scenario</option>
            </select>
          </label>
        </div>
        <div className="sbb-instant" aria-label="Ungated indicative results" data-testid="engine-instant">
          <div><small>Gross project sensitivity</small><strong>{money(result.grossCost * 0.85)}–{money(result.grossCost * 1.15)}</strong><span>±15% cost scenario, not a confidence interval</span></div>
          <div><small>Potential ownership credit sensitivity</small><strong>{x.financing === "ppa" ? "Retained by system owner" : `${money(result.grossCost * 0.06)}–${money(result.grossCost * 0.3)}`}</strong><span>6%–30% rate scenarios; eligibility and qualified basis required</span></div>
          <div><small>Annual net savings sensitivity</small><strong>{money(result.annualNetOperatingSavings * 0.8)}–{money(result.annualNetOperatingSavings * 1.2)}</strong><span>±20% operating scenario before financing and tax</span></div>
        </div>
        <div className="sbb-pillars">
          <article id="pillar-tax" className="sbb-card">
            <span className="sbb-number">01 / POTENTIAL TAX BENEFITS</span><h3>Keep more capital in Year 1.</h3>
            <strong className="sbb-kpi"><AnimatedMoney value={result.taxShield} /></strong>
            <p>
              {isExample
                ? "Example value — the example buyer has confirmed eligibility. Your own scenario starts at $0 until eligibility is confirmed with your tax adviser."
                : x.financing === "ppa"
                  ? "PPA: ownership incentives are not assigned to the customer."
                  : result.taxBenefitsApplied
                    ? "Scenario credit + deduction tax value."
                    : "Showing $0 because eligibility is unconfirmed. Confirm it in “Adjust my financial assumptions” to see a scenario — a real result needs your tax adviser."}
            </p>
            <Tip label="Why this starts at $0 — and what changes it">The model never assumes you qualify. When you and your tax adviser confirm eligibility and usable tax capacity, the scenario applies the credit and deduction rates below. The adviser — not this site — confirms rates, timing, qualified basis and recapture.</Tip>
            <button type="button" onClick={checkEligibility} data-testid="engine-check-eligibility">{isExample ? "Check eligibility for my building →" : "Check eligibility →"}</button>
          </article>
          <article id="pillar-demand" className="sbb-card">
            <span className="sbb-number">02 / DEMAND-CHARGE REDUCTION</span><h3>Target expensive power peaks.</h3>
            <strong className="sbb-kpi"><AnimatedMoney value={result.demandSavings} /><small> / year</small></strong>
            <p>Modeled at $0 until an interval-data study confirms the peaks — then your entered annual scenario applies.</p>
            <Tip label="How demand savings differ from energy savings">Energy savings come from using less grid electricity overall (kWh). Demand savings come from cutting your single highest power draw (kW) in the billing window — batteries can flatten those peaks. Utilities measure demand differently, so your tariff and interval data decide the real number.</Tip>
            <button type="button" onClick={() => openAudit(snapshot)}>Check my demand charges →</button>
          </article>
          <article id="pillar-capital" className="sbb-card">
            <span className="sbb-number">03 / OWNERSHIP & FINANCING</span><h3>Compare ownership with financing.</h3>
            <strong className="sbb-kpi"><AnimatedMoney value={result.yearOneOperatingSavings} /><small> / year</small></strong>
            <p>{x.financing === "cash" ? "Year-1 operating savings before tax and upfront system cost." : "Operating savings only — financed and PPA cash flows need a project-specific payment schedule."}</p>
            <Tip label="Operating savings vs cash flow after financing">Operating savings are what the system saves before any financing payment. Cash flow subtracts what you actually pay. A financed or PPA project can have positive cash flow while transferring more of the long-term benefit to the financier — the comparison shows the trade-off.</Tip>
            <button type="button" onClick={() => openAudit(snapshot)}>Compare cash, C-PACE & PPA →</button>
          </article>
          <article id="pillar-return" className="sbb-card">
            <span className="sbb-number">04 / PAYBACK & LONG-TERM RETURNS</span><h3>See when the numbers turn.</h3>
            <strong className="sbb-kpi">{result.simplePayback == null ? (result.paybackSuppressed ? "Needs an ownership model" : "Not reached in 25 years") : `${result.simplePayback.toFixed(1)} years`}</strong>
            <p>{isExample ? "Example payback under the example's assumptions, including its confirmed eligibility. " : ""}Your payback follows your property, utility tariff, project cost and eligibility — enter your real numbers for a meaningful result.</p>
            <Tip label="Which assumptions determine payback">After-tax payback uses modeled operating savings, usable first-year tax benefits (ownership paths) and the entered cost, with degradation, escalation and O&M applied. It is a simple payback, not an IRR or a discounted payback. Every input is adjustable below — change nothing to inflate the result; we don't.</Tip>
            <p className="sbb-value">{result.npv25 == null ? "25-year cash NPV applies to ownership paths." : `${money(result.npv25)} 25-year cash NPV at 8% discount — not a guaranteed return.`}</p>
            <button type="button" onClick={() => openAudit(snapshot)}>Request my CFO pro-forma →</button>
          </article>
        </div>
        <details className="sbb-reading">
          <summary>Reading these numbers — plain-English guide</summary>
          <ul>
            <li><b>Example vs your numbers:</b> the example assumes confirmed eligibility; your own scenario starts at $0 until you confirm eligibility under “Adjust my financial assumptions.” A real result requires your tax adviser to confirm eligibility, timing and tax capacity.</li>
            <li><b>What drives payback:</b> your actual bills, the project cost entered, how much solar energy is used onsite, demand-charge savings, and usable tax benefits on an ownership path.</li>
            <li><b>Operating savings vs cash flow:</b> operating savings ignore financing payments; cash flow includes them. A low monthly payment can look attractive while extending total cost.</li>
            <li><b>Demand vs energy savings:</b> energy savings reduce total kWh purchased; demand savings reduce billed peak kW. Batteries target the second, and your utility's measurement method decides how much is recoverable.</li>
            <li><b>Ownership vs PPA tax treatment:</b> on a PPA you buy power; the system owner claims the tax benefits and prices them into the rate. Ownership paths keep those benefits with you if you qualify — never both.</li>
          </ul>
        </details>
        <details className="sbb-assumptions" id="pillar-assumptions" open={assumptionsOpen} onToggle={(e) => setAssumptionsOpen(e.target.open)}>
          <summary>Adjust my financial assumptions</summary>
          <div className="sbb-form-grid">
            {numberField("Installed solar cost ($/W)", "dollarsPerWatt", 0.5, 10, 0.05)}
            {numberField("Roof capacity (W / usable sq ft)", "roofWattsPerSqFt", 1, 20, 0.5)}
            {numberField("Annual yield (kWh / kW)", "annualKWhPerKW", 500, 2200, 10)}
            {numberField("Energy rate ($/kWh)", "energyRate", 0.01, 1, 0.01)}
            <PercentField id={`${id}-self`} label="Solar energy used onsite" hint="Example: 90% means most generation is consumed in the building." value={x.selfConsumption} onChange={(v) => update("selfConsumption", v)} />
            <PercentField id={`${id}-demand`} label="Share of bill from demand charges" hint="Example: 30% of the bill is demand-based. Not sure? Leave at 30% and upload your bill." value={x.demandShare} onChange={(v) => update("demandShare", v)} />
            {numberField("Annual demand savings scenario ($)", "annualDemandSavings", 0, 10000000, 100)}
            <label className="sbb-check"><input type="checkbox" checked={Boolean(x.intervalStudyConfirmed)} onChange={(e) => update("intervalStudyConfirmed", e.target.checked)} />Scenario only: an interval-data study has confirmed this demand scenario.</label>
            {numberField("Battery installed cost ($; 0 = no battery)", "batteryCost", 0, 10000000, 1000)}
            <PercentField id={`${id}-opex`} label="Annual operating cost (of project cost)" hint="Monitoring, insurance and upkeep scenario." value={x.annualOperatingCostFraction} onChange={(v) => update("annualOperatingCostFraction", v)} max={20} step={0.5} />
            <PercentField id={`${id}-tax`} label="Marginal business tax rate" hint="Not sure? Ask your tax adviser — the model stays at 21% as an illustration only." value={x.taxRate} onChange={(v) => update("taxRate", v)} max={60} />
            <PercentField id={`${id}-credit`} label="Investment credit scenario" hint="Rate scenarios require adviser confirmation of current rules." value={x.creditRate} onChange={(v) => update("creditRate", v)} max={50} />
            <PercentField id={`${id}-deduction`} label="First-year deduction (of eligible basis)" hint="Depreciation timing assumption — adviser must confirm." value={x.firstYearDeductionFraction} onChange={(v) => update("firstYearDeductionFraction", v)} />
            {x.financing === "cpace" && numberField("C-PACE annual payment ($)", "annualFinancingPayment", 0, 10000000, 100)}
            {x.financing === "ppa" && numberField("PPA annual payment incl. storage ($)", "annualPpaPayment", 0, 10000000, 100)}
            <PercentField id={`${id}-cap`} label="Property cap rate (for value sensitivity)" hint="Used only for the value-sensitivity note; not an appraisal." value={x.capRate} onChange={(v) => update("capRate", v)} min={1} max={20} step={0.5} />
          </div>
          <label className="sbb-check"><input type="checkbox" checked={x.eligibilityConfirmed} onChange={(e) => update("eligibilityConfirmed", e.target.checked)} />Scenario only: my adviser has confirmed the ownership credit and depreciation assumptions for this exploration.</label>
          <label className="sbb-check"><input type="checkbox" checked={x.taxBenefitsUsable} onChange={(e) => update("taxBenefitsUsable", e.target.checked)} />Scenario only: the modeled credit and deduction could be used in Year 1.</label>
          <label className="sbb-check"><input type="checkbox" checked={x.ownerPaysPower} onChange={(e) => update("ownerPaysPower", e.target.checked)} />The property owner bears these utility costs and keeps the modeled savings.</label>
          <p className="sbb-hint">These assumptions are editable and illustrative — we do not change them to produce attractive payback numbers. Solar and battery qualified tax bases may differ. Financing figures require a quote; a zero payment entry is not a financing offer. Residential tax rules are separate.</p>
          {content.assumptionSources ? <p className="sbb-hint" data-testid="assumption-sources">{content.assumptionSources}</p> : null}
        </details>
        <aside className="sbb-precision">
          <span className="sbb-eyebrow">YOUR BILL ADDS THE MISSING DETAIL</span>
          <h2>Trade assumptions for a bill-based review.</h2>
          <p>TOU periods, demand tiers and ratchets can change the outcome. Upload one recent bill or up to 12 months — PDF, or a clear photo of your bill. We'll confirm the next steps and review timeline after receiving your bill.</p>
          <div className="sbb-unlock"><strong>Unlock your Solar Black Belt audit milestone</strong>
            <span>Engineering review request + executive-ready presentation after validation.</span></div>
          <DropZoneLite className="sbb-drop" maxSize={15 * 1024 * 1024} maxFiles={12}
            ariaLabel="Choose or drop utility bill files (PDF, JPG or PNG) to begin the audit" testId="engine-dropzone"
            onDrop={(accepted) => openAudit(snapshot, accepted)}>
            {(dragActive) => (<>
              <strong>{dragActive ? "Drop your bills here" : "Drop your bill files here"}</strong>
              <span>or click to choose · PDF, JPG or PNG · up to 12 files, 15 MB each · No payment required</span>
            </>)}
          </DropZoneLite>
          <button type="button" className="sbb-primary" onClick={() => openAudit(snapshot)} data-testid="precision-upload">Upload Your Commercial Electric Bill</button>
          <p className="sbb-hint">Your results depend on your property, utility tariff, project cost and eligibility. We'll confirm the review timeline after receiving your bill.</p>
        </aside>
      </div>
    </section>
  );
}
